/// <reference types="@types/google.maps" />
import * as React from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

import locationSvg from '@/assets/food/icons/location.svg'
import type {UserAddressComponentProps} from '@/lib/food-grubhub/foodGrubhubModel'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import {FoodGrubhubRoute} from '@/routeRegistry'
import useSetSearch from '@/utils/router/useSetSearch'

import style from './FoodGrubhubSearchWithAutoComplete.module.scss'

interface SearchInputProps {
  setResetInput?: (value: boolean) => void
}

export default memo(function FoodGrubhubSearchWithAutoCompletee({setResetInput}: SearchInputProps) {
  const {placesService, placePredictions, getPlacePredictions} = usePlacesService({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  })
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [_storageUserLocation, setStorageUserLocation] = useGrubhubUserAddress()

  const setLongitude = useSetSearch(FoodGrubhubRoute.id, 'lng')
  const setLatitude = useSetSearch(FoodGrubhubRoute.id, 'lat')
  const setAddress = useSetSearch(FoodGrubhubRoute.id, 'address')

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const getShortNameByType = (components: UserAddressComponentProps[], type: string) => {
    const component = components.find(comp => comp.types?.includes(type))
    return component ? component.short_name : null
  }

  const handleSelect = (placeId: string) => {
    if (!placesService) return

    placesService.getDetails({placeId}, (placeDetails, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && placeDetails) {
        const lat = placeDetails.geometry?.location?.lat()
        const lng = placeDetails.geometry?.location?.lng()
        setLongitude(lng)
        setLatitude(lat)
        setAddress(placeDetails.formatted_address)
        setStorageUserLocation({
          latitude: lat ?? 0,
          longitude: lng ?? 0,
          address: placeDetails.formatted_address ?? '',
          address_detail: {
            street_number:
              getShortNameByType(placeDetails.address_components ?? [], 'street_number') ?? '',
            route: getShortNameByType(placeDetails.address_components ?? [], 'route') ?? '',
            locality: getShortNameByType(placeDetails.address_components ?? [], 'locality') ?? '',
            administrative_area_level_1:
              getShortNameByType(
                placeDetails.address_components ?? [],
                'administrative_area_level_1',
              ) ?? '',
            country: getShortNameByType(placeDetails.address_components ?? [], 'country') ?? '',
            postal_code:
              getShortNameByType(placeDetails.address_components ?? [], 'postal_code') ?? '',
          },
        })
        setInputValue(placeDetails.formatted_address ?? '')
        getPlacePredictions({input: ''})
      } else {
        console.error('Failed to fetch details:', status)
      }
    })

    setResetInput?.(false)
  }

  const handleSearchClick = () => {
    if (!inputValue) return

    const geocoder = new google.maps.Geocoder()

    geocoder.geocode(
      {address: inputValue, componentRestrictions: {country: 'US'}},
      (results, status) => {
        if (results && results.length > 0) {
          const result = results[0]
          const lat = result?.geometry.location.lat()
          const lng = result?.geometry.location.lng()
          setLongitude(lng)
          setLatitude(lat)
          setAddress(result?.formatted_address)
          setStorageUserLocation({
            latitude: lat ?? 0,
            longitude: lng ?? 0,
            address: result?.formatted_address ?? '',
            address_detail: {
              street_number:
                getShortNameByType(result?.address_components ?? [], 'street_number') ?? '',
              route: getShortNameByType(result?.address_components ?? [], 'route') ?? '',
              locality: getShortNameByType(result?.address_components ?? [], 'locality') ?? '',
              administrative_area_level_1:
                getShortNameByType(
                  result?.address_components ?? [],
                  'administrative_area_level_1',
                ) ?? '',
              country: getShortNameByType(result?.address_components ?? [], 'country') ?? '',
              postal_code:
                getShortNameByType(result?.address_components ?? [], 'postal_code') ?? '',
            },
          })

          setInputValue(result?.formatted_address ?? '')
        } else {
          console.error('Geocoding failed:', status)
        }
      },
    )

    setResetInput?.(false)
  }

  return (
    <div className={style.SearchInputContainer}>
      <div className={style.SearchInputContent}>
        <div className={style.SearchInput}>
          <img src={locationSvg} alt='' className={style.SearchInputIcon} />
          <input
            ref={inputRef}
            placeholder='Enter your delivery address'
            className={style.SearchInputField}
            value={inputValue}
            onChange={evt => {
              const val = evt.target.value
              setInputValue(val)
              getPlacePredictions({input: val, componentRestrictions: {country: 'us'}})
            }}
          />
        </div>
        {placePredictions.length > 0 && (
          <div className={style.SuggestionsContainer}>
            {placePredictions.map(suggestion => (
              <div
                key={suggestion.place_id}
                role='button'
                tabIndex={0}
                onClick={() => {
                  handleSelect(suggestion.place_id)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleSelect(suggestion.place_id)
                  }
                }}
                className={style.SuggestValue}
              >
                {/* {item.description} */}
                <img className={style.SuggestIcon} src={locationSvg} alt='' />
                <div className={style.SuggestText}>{suggestion.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <button
        type='button'
        className={style.SearchBtn}
        aria-hidden='true'
        onClick={handleSearchClick}
      >
        Search
      </button>
    </div>
  )
})
